import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useSite } from "@/lib/site";
import { PageHeader, Reveal } from "@/components/site/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const cls = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 outline-none focus:border-blue-500 transition-colors";

const ContactForm = () => {
  const { packages } = useSite();
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", phone: "", service: params.get("service") || "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const options = [...new Set([...packages.map((p) => p.name), f.service, "Something else"].filter(Boolean))];
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/contact", f); setDone(true); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (done) return (
    <div className="rounded-3xl bg-white border border-slate-200 p-10 text-center" data-testid="contact-success">
      <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
      <h2 className="mt-5 font-display text-2xl font-bold">Thanks, {f.name.split(" ")[0]}!</h2>
      <p className="mt-2 text-slate-600">Your message is in. We'll get back to you at {f.email} shortly.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Your name" value={f.name} onChange={set("name")} className={cls} data-testid="contact-name-input" />
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={cls} data-testid="contact-email-input" />
        <input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className={cls} data-testid="contact-phone-input" />
        <Select value={f.service} onValueChange={(v) => setF({ ...f, service: v })}>
          <SelectTrigger className="h-auto rounded-2xl border-slate-200 bg-white px-4 py-3.5 text-base" data-testid="contact-service-select"><SelectValue placeholder="Service of interest" /></SelectTrigger>
          <SelectContent>{options.map((o) => <SelectItem key={o} value={o} data-testid={`contact-service-option-${o.toLowerCase().replace(/\W+/g, "-")}`}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <textarea required rows={6} placeholder="Tell us about your business and goals" value={f.message} onChange={set("message")} className={`${cls} resize-none`} data-testid="contact-message-input" />
      <button disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 font-semibold transition-colors disabled:opacity-60" data-testid="contact-form-submit-btn">
        {busy ? "Sending…" : "Send message"}
      </button>
    </form>
  );
};

export default function Contact() {
  const { content } = useSite();
  const phone = content.phone || "+1 323 786 1615";
  return (
    <>
      <PageHeader overline="Contact" title="Let's build what's next." testid="contact-header">
        Tell us where you want to grow. We reply to every message — usually within one business day.
      </PageHeader>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7"><ContactForm /></Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 space-y-4">
          <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} data-testid="contact-phone-link" className="group block rounded-3xl bg-[#090D16] text-white p-7 hover:bg-blue-600 transition-colors">
            <Phone className="w-6 h-6 text-sky-400 group-hover:text-white" />
            <p className="mt-6 text-sm text-slate-400 group-hover:text-blue-100">Call us</p>
            <p className="font-display text-2xl sm:text-3xl font-bold">{phone}</p>
          </a>
          <a href={`mailto:${content.email}`} data-testid="contact-email-link" className="block rounded-3xl bg-white border border-slate-200 p-7 hover:border-blue-500 transition-colors">
            <Mail className="w-6 h-6 text-blue-600" />
            <p className="mt-6 text-sm text-slate-500">Email</p>
            <p className="font-display text-xl font-bold text-slate-900 break-all">{content.email}</p>
          </a>
          <div className="rounded-3xl bg-blue-50 border border-blue-100 p-7">
            <MessageCircle className="w-6 h-6 text-blue-600" />
            <p className="mt-4 font-display font-bold text-slate-900">Prefer to chat?</p>
            <p className="text-sm text-slate-600 mt-1">Tap the chat bubble at the bottom right to ask a quick question.</p>
          </div>
        </Reveal>
      </section>
    </>
  );
}
